import {Box, CircularProgress, FormControl, InputLabel, MenuItem, Select} from '@mui/material';
import { useTranslation } from "react-i18next";
import {TemplateData, TemplateMetadata} from "../../pages/app/types.ts";
import {api} from "../../api/api.ts";
import {useAuth} from "../../context/auth/useAuth.ts";
import {useState} from "react";

interface TemplateSelectionStepProps {
    templates: TemplateMetadata[];
    blueprints: TemplateMetadata[];
    template: TemplateData;
    setTemplate: (template: TemplateData) => void;
    handleNext: () => void;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const TemplateSelectionStep = ({templates, blueprints, template, setTemplate, handleNext }: TemplateSelectionStepProps) => {
    const { t } = useTranslation();
    const user = useAuth();
    const [loading, setLoading] = useState(false);

    const handleTemplateChange = (selectedValue: string) => {
        setLoading(true);
        const selected =
            blueprints.find((b) => b.templateID.toString() === selectedValue) ||
            templates.find((t) => t.templateID.toString() === selectedValue);

        if(user && selected) {
            // @ts-ignore
            api.getTemplateData(user.subID, selected.templateID)
                .then((data) => {
                    setTemplate({
                        ...template,
                        name: selected?.templateName || '',
                        measurementPoints: data?.measurementPoints || [],
                        template: data?.template || '',
                    });
                    setLoading(false);
                    handleNext();
                })
                .catch((error) => {
                    console.error('Error fetching template data:', error);
                    setLoading(false);
                })
        }
    }

    return (
        <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            {loading? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <CircularProgress sx={{flex: 1, width: '100%', alignSelf: 'center'}}/>
                </Box>
            ) : (
                <FormControl fullWidth>
                    <InputLabel>{t('stepper.step.template.label')}</InputLabel>
                    <Select
                        label={t('stepper.step.template.select')}
                        value={template?.id?.toString() ?? ''}
                        onChange={(e) => handleTemplateChange(e.target.value)}
                        MenuProps={MenuProps}
                    >
                        <MenuItem disabled value="">
                            <em>Blueprints</em>
                        </MenuItem>
                        {blueprints.map((blueprint) => (
                            <MenuItem key={blueprint.templateID} value={blueprint.templateID.toString()}>
                                {blueprint.templateName}
                            </MenuItem>
                        ))}
                        {templates.length > 0 && (
                            <MenuItem disabled value="">
                                <em>Templates</em>
                            </MenuItem>
                        )}
                        {templates.map((template) => (
                            <MenuItem key={template.templateID} value={template.templateID.toString()}>
                                {template.templateName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
};

export default TemplateSelectionStep;
